/**
 * Mode-driven structured logger with auto-selected detail level
 * DEV: Full traces with values, PRO: Concise production-safe summaries
 */

import { isDevMode } from './dev/devHelpers';
import { healthState } from '$lib/state/health.state.svelte';

// Log levels
export enum LogLevel {
	TRACE = 0,
	DEBUG = 1,
	INFO = 2,
	WARN = 3,
	ERROR = 4
}

// Log policies by mode
const LOG_POLICIES = {
	dev: {
		minLevel: LogLevel.TRACE,
		namespaces: ['*'], // Allow all
		maxPayloadSize: 3072, // 3KB
		includePayloads: true,
		sampling: 1 // Log everything
	},
	pro: {
		minLevel: LogLevel.INFO,
		namespaces: ['alerts.modal', 'alerts.chart', 'alerts.service', 'ws'],
		maxPayloadSize: 300,
		includePayloads: false,
		sampling: 0.1 // Sample 1 in 10 for frequent events
	}
};

// Redacted keys for security
const REDACTED_KEYS = new Set([
	'authorization',
	'token',
	'apiKey',
	'password',
	'secret',
	'credential'
]);

// Sampling counters for PRO mode
const samplingCounters = new Map<string, number>();

// Cache runtime mode
let cachedMode: 'dev' | 'pro' | null = null;

/**
 * Get a readable timestamp for timing analysis (MM-DD HH:mm:ss.SSS)
 * Format: 12-03 14:30:45.123
 */
export function getTimestamp(): string {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const day = String(now.getDate()).padStart(2, '0');
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');
	const seconds = String(now.getSeconds()).padStart(2, '0');
	const ms = String(now.getMilliseconds()).padStart(3, '0');
	return `${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;
}
let cacheTime = 0;
const CACHE_TTL = 60000; // 1 minute

/**
 * Get runtime mode from health store or fallback to isDevMode
 */
function getRuntimeMode(): 'dev' | 'pro' {
	const now = Date.now();

	// Return cached value if still valid
	if (cachedMode && now - cacheTime < CACHE_TTL) {
		return cachedMode;
	}

	try {
		const isDev =
			isDevMode() ||
			healthState.isDevelopment ||
			healthState.healthStatus?.environment?.mode === 'development';
		cachedMode = isDev ? 'dev' : 'pro';
		cacheTime = now;
		return cachedMode;
	} catch {
		// Fallback to isDevMode() if health state read fails
		cachedMode = isDevMode() ? 'dev' : 'pro';
		cacheTime = now;
		return cachedMode;
	}
}

interface LogContext {
	[key: string]: any;
}

interface FlowTracer {
	start(step: string, ctx?: LogContext): void;
	step(step: string, ctx?: LogContext): void;
	complete(metadata?: LogContext): void;
	end(step?: string, ctx?: LogContext): void;
}

interface Logger {
	trace(message: string, context?: LogContext): void;
	debug(message: string, context?: LogContext): void;
	info(message: string, context?: LogContext): void;
	warn(message: string, context?: LogContext): void;
	error(message: string, context?: LogContext): void;
	group(message: string, context?: LogContext): void;
	groupEnd(message: string): void;
	time(label: string): void;
	timeEnd(label: string): void;
	withCtx(baseCtx: LogContext): (ctx?: LogContext) => LogContext;
	flow(flowName: string): FlowTracer;
}

/**
 * Truncate string to max length
 */
function truncate(str: string, maxLen: number): string {
	if (str.length <= maxLen) return str;
	return str.substring(0, maxLen - 3) + '...';
}

/**
 * Redact sensitive values from object
 */
function redactSensitive(obj: any, mode: 'dev' | 'pro'): any {
	if (typeof obj !== 'object' || obj === null) return obj;

	const result: any = Array.isArray(obj) ? [] : {};

	for (const [key, value] of Object.entries(obj)) {
		// Check if key should be redacted
		if (REDACTED_KEYS.has(key.toLowerCase())) {
			result[key] = '[REDACTED]';
		} else if (typeof value === 'object' && value !== null) {
			result[key] = redactSensitive(value, mode);
		} else if (typeof value === 'string') {
			// In PRO mode, truncate long strings
			const maxLen = mode === 'pro' ? 50 : 500;
			result[key] = truncate(value, maxLen);
		} else {
			result[key] = value;
		}
	}

	return result;
}

/**
 * Apply truncation and redaction based on mode
 */
export function safeCtx(obj: any, mode?: 'dev' | 'pro'): any {
	const actualMode = mode ?? getRuntimeMode();
	const policy = LOG_POLICIES[actualMode];

	// Stringify and check size
	let safe = redactSensitive(obj, actualMode);
	const str = JSON.stringify(safe);

	if (str.length > policy.maxPayloadSize) {
		// For PRO mode, just return a summary
		if (actualMode === 'pro') {
			return {
				_truncated: true,
				keys: Object.keys(obj).slice(0, 5),
				size: str.length
			};
		}
		// For DEV mode, truncate the object
		return { ...safe, _truncated: true };
	}

	return safe;
}

/**
 * Check if should log based on namespace and level
 */
function shouldLog(namespace: string, level: LogLevel): boolean {
	// Check for emergency override via localStorage (browser only)
	if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
		const debugOverride = localStorage.getItem('DEBUG');
		if (debugOverride) {
			const namespaces = debugOverride.split(',').map((s) => s.trim());
			if (namespaces.includes('*') || namespaces.includes(namespace)) {
				return true;
			}
		}
	}

	const mode = getRuntimeMode();
	const policy = LOG_POLICIES[mode];

	// Check level
	if (level < policy.minLevel) return false;

	// Check namespace
	if (policy.namespaces.includes('*')) return true;
	return policy.namespaces.includes(namespace);
}

/**
 * Check sampling for frequent events in PRO mode
 */
function shouldSample(namespace: string): boolean {
	const mode = getRuntimeMode();
	if (mode === 'dev') return true;

	const policy = LOG_POLICIES.pro;
	const key = `${namespace}:sample`;
	const count = (samplingCounters.get(key) || 0) + 1;
	samplingCounters.set(key, count);

	// Sample based on policy
	return count % Math.round(1 / policy.sampling) === 0;
}

/**
 * Create a logger for a specific namespace
 */
export function createLogger(namespace: string): Logger {
	const mode = getRuntimeMode();
	const policy = LOG_POLICIES[mode];

	const log = (level: LogLevel, levelName: string, message: string, context?: LogContext) => {
		if (!shouldLog(namespace, level)) return;

		// Apply sampling for INFO level in PRO mode
		if (mode === 'pro' && level === LogLevel.INFO && !shouldSample(namespace)) {
			return;
		}

		const timestamp = new Date().toISOString();
		const safeContext = context ? safeCtx(context, mode) : undefined;

		// Format based on mode
		if (mode === 'pro') {
			// Concise format for production
			const parts = [`[${namespace}]`, message];
			if (safeContext && policy.includePayloads === false) {
				// Only include IDs and counts
				const summary = {
					id: safeContext.id || safeContext.alert_flag_id,
					count: safeContext.count,
					time: safeContext.time
				};
				parts.push(JSON.stringify(summary));
			}
			const consoleMethod = levelName === 'trace' ? 'debug' : levelName;
			(console as any)[consoleMethod](parts.join(' '));
		} else {
			// Detailed format for development
			const consoleMethod = levelName === 'trace' ? 'debug' : levelName;
			(console as any)[consoleMethod](`[${namespace}] ${message}`, safeContext ? safeContext : '');
		}
	};

	return {
		trace: (msg, ctx) => log(LogLevel.TRACE, 'trace', msg, ctx),
		debug: (msg, ctx) => log(LogLevel.DEBUG, 'debug', msg, ctx),
		info: (msg, ctx) => log(LogLevel.INFO, 'info', msg, ctx),
		warn: (msg, ctx) => log(LogLevel.WARN, 'warn', msg, ctx),
		error: (msg, ctx) => log(LogLevel.ERROR, 'error', msg, ctx),

		group: (message: string, context?: LogContext) => {
			if (!shouldLog(namespace, LogLevel.INFO)) return;

			const safeContext = context ? safeCtx(context, mode) : undefined;

			if (mode === 'pro') {
				// Single line in production
				console.info(`[${namespace}] ${message}`, safeContext || '');
			} else {
				// Grouped in development
				console.groupCollapsed(`[${namespace}] ${message}`, safeContext || '');
			}
		},

		groupEnd: (message: string) => {
			if (!shouldLog(namespace, LogLevel.INFO)) return;
			if (mode === 'dev') {
				console.groupEnd();
			}
		},

		time: (label: string) => {
			if (mode === 'dev') console.time(`[${namespace}] ${label}`);
		},

		timeEnd: (label: string) => {
			if (mode === 'dev') console.timeEnd(`[${namespace}] ${label}`);
		},

		withCtx: (baseCtx: LogContext) => (ctx?: LogContext) => ({ ...baseCtx, ...ctx }),

		flow: (flowName: string): FlowTracer => {
			return traceFlow(namespace, { flow: flowName });
		}
	};
}

/**
 * Create a flow tracer for structured sequences
 */
export function traceFlow(namespace: string, flowCtx: LogContext): FlowTracer {
	const logger = createLogger(namespace);
	const mode = getRuntimeMode();

	return {
		start: (step: string, ctx?: LogContext) => {
			const fullCtx = { ...flowCtx, ...ctx, step };
			if (mode === 'dev') {
				logger.group(step, fullCtx);
			} else {
				logger.info(`${step}.start`, fullCtx);
			}
		},

		step: (step: string, ctx?: LogContext) => {
			const fullCtx = { ...flowCtx, ...ctx, step };
			logger.info(step, fullCtx);
		},

		complete: (metadata?: LogContext) => {
			const fullCtx = { ...flowCtx, ...metadata };
			if (mode === 'dev') {
				logger.info('Flow completed', fullCtx);
				logger.groupEnd('');
			} else {
				logger.info('complete', fullCtx);
			}
		},

		end: (step?: string, ctx?: LogContext) => {
			const fullCtx = { ...flowCtx, ...ctx };
			if (mode === 'dev') {
				if (step) logger.info(step, fullCtx);
				logger.groupEnd('');
			} else if (step) {
				logger.info(`${step}.end`, fullCtx);
			}
		}
	};
}
